<template>
  <div>

    <p class="text-center text--secondary mb-8" style="font-size: 15px; line-height: 1.6;">
      {{ $t('2fa.method.email.register.verification_code_description') }}
    </p>

    <v-alert v-if="error" type="error" dense text dismissible @input="error = null" class="mb-4">
      {{ error }}
    </v-alert>

    <v-text-field
        v-model="code"
        :label="$t('2fa.method.register.verification_code')"
        outlined
        dense
        hide-details="auto"
        :maxlength="codeLength"
        :counter="codeLength"
        :rules="[rules.required, rules.alphanumeric, rules.length(codeLength)]"
        :loading="twoFactorAuth.verificationLoading"
        :disabled="twoFactorAuth.verificationLoading"
        @keydown.enter="verify"
        autofocus
        autocomplete="one-time-code"
        class="mb-5 text-uppercase"
    ></v-text-field>

    <v-btn
        color="primary"
        depressed
        block
        large
        @click="verify"
        :loading="twoFactorAuth.verificationLoading"
        :disabled="!isCodeValid"
        class="text-none mb-4"
        style="font-weight: 500; letter-spacing: 0;"
    >
      {{ $t('buttons.verify') }}
    </v-btn>

    <v-divider class="my-4"></v-divider>

    <div class="d-flex justify-center align-center">
      <v-btn
          text
          small
          @click="resend"
          :disabled="resendCooldown > 0 || twoFactorAuth.verificationLoading"
          class="text-none mr-2"
          color="grey darken-1"
      >
        {{ resendCooldown > 0 ? `${$t('buttons.resend')} (${resendCooldown}s)` : $t('buttons.resend') }}
      </v-btn>

      <v-btn
          v-if="canCancel"
          text
          small
          @click="cancel"
          :disabled="twoFactorAuth.verificationLoading"
          class="text-none"
          color="grey darken-1"
      >
        {{ $t('buttons.cancel') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import {twoFactorAuthStore} from "@/store/pinia/twoFactorAuthStore";

export default {
  name: 'EmailMethodInstructions',
  props: {
    instructionsData: {
      type: Object,
      required: false,
      default: null
    },
    method: {
      type: Object,
      required: true,
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const twoFactorAuth = twoFactorAuthStore();
    return {twoFactorAuth};
  },
  data() {
    return {
      code: '',
      error: null,
      resendCooldown: 0,
      cooldownInterval: null,
      rules: {
        required: value => !!value || this.$t('validation.required'),
        alphanumeric: value => /^[a-zA-Z0-9]+$/.test(value) || this.$t('validation.alphanumeric'),
        length: len => value => (value && value.length === len) || this.$t('validation.code_length', {length: len}),
      },
    };
  },
  computed: {
    codeLength() {
      return 8;
    },
    isCodeValid() {
      return /^[a-zA-Z0-9]{8}$/.test(this.code);
    }
  },
  methods: {
    async verify() {
      if (!this.isCodeValid) return;
      this.error = null;

      try {
        const payload = await this.twoFactorAuth.checkRegister2FAMethodCode(this.code);

        if (payload.data.payload) {
          this.$emit('completed', payload);
          this.code = '';
          return;
        }
        this.error = this.$t('2fa.method.register.verify.failed');

      } catch (e) {
        this.error = e.response?.data?.message || this.$t('2fa.method.register.verify.error');
      }
    },

    async resend() {
      if (this.resendCooldown > 0) return;
      this.error = null;
      this.startResendCooldown();

      try {
        await this.twoFactorAuth.resendRegister2FAEmailCode();
      } catch (e) {
        this.resendCooldown = 0;
        clearInterval(this.cooldownInterval);
        this.error = e.response?.data?.message || this.$t('2fa.method.register.resend.error');
      }
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      this.cooldownInterval = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownInterval);
        }
      }, 1000);
    },

    cancel() {
      this.$emit('cancel');
    },
  },

  mounted() {
    this.$emit('title-change', this.$t('2fa.method.email.register.title'));
    // Backend already sends the email during /auth/2fa/method/register — just start the cooldown
    if (this.method?.is_supports_resend) {
      this.startResendCooldown();
    }
  },

  beforeDestroy() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.12);
}

::v-deep .v-counter {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

::v-deep .text-uppercase input {
  text-transform: uppercase;
}
</style>
